/**
 * ErrorBoundary — catches render errors in any subtree so the page never goes
 * blank. Especially valuable for the Analytics & AI tab where a single chart
 * library hiccup or unexpected response shape used to crash the whole page.
 *
 * Usage:
 *   <ErrorBoundary label="Analytics & AI">
 *     <AnalyticsDashboard ... />
 *   </ErrorBoundary>
 */
import React from 'react';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to the JS console so administrators can diagnose from
    // browser DevTools even on production.
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${this.props.label || 'app'}]`, error, info);
    this.setState({ info });
  }

  reset = () => this.setState({ hasError: false, error: null, info: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    const isAdmin = (() => {
      try {
        return JSON.parse(localStorage.getItem('user') || '{}')?.role === 'administrator';
      } catch { return false; }
    })();
    return (
      <div
        className="border border-red-200 bg-red-50/50 rounded-lg p-6 m-4"
        data-testid={`error-boundary-${(this.props.label || 'app').toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-red-900">
              {this.props.label || 'This section'} could not be displayed
            </h3>
            <p className="text-sm text-red-800 mt-1">
              An unexpected error occurred while rendering this view. The rest of the application
              continues to work — try refreshing this section or come back later.
            </p>
            {isAdmin && this.state.error && (
              <pre className="mt-3 p-3 bg-white border border-red-200 rounded text-xs text-red-700 whitespace-pre-wrap break-words max-h-48 overflow-auto">
                {String(this.state.error?.message || this.state.error)}
                {this.state.info?.componentStack ? `\n\nComponent stack:${this.state.info.componentStack}` : ''}
              </pre>
            )}
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={this.reset} data-testid="error-boundary-retry">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Try again
              </Button>
              <Button size="sm" variant="ghost" onClick={() => window.location.reload()} data-testid="error-boundary-reload">
                Reload page
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
